// lib/continuum/simulation/dynamics-registry.js
// ═══════════════════════════════════════════════════════════
// THE 30-DYNAMIC DECLARATIVE REGISTRY
//
// Every dynamic the project tests is defined here as data, NOT
// as hand-coded JS. The generic engine in engine.js reads this
// registry and runs each dynamic without per-dynamic code.
//
// Adding a new dynamic = adding a config entry here. The engine
// handles loading, filtering, computing, comparing, and returning
// events. Per-dynamic JS is only needed when a dynamic uses a
// metric type the engine doesn't yet support (rare).
//
// Schema for each dynamic:
//
//   id, label, short, cluster, version, status, units, description
//
//   metric: {
//     type: 'ratio' | 'csd_composite' | 'dip_then_rebound' |
//           'asymmetry_ratio' | 'pending',
//     ...metric-specific config (numerator/denominator for ratio,
//        crash_floor for dip_then_rebound, weights for csd, etc.)
//   }
//
//   filters: [
//     { field, op, value | expression }   — applied to raw rows
//                                          before metric computation
//   ]
//
//   domains: {
//     eco:   { sources: [...], system_type, label },
//     human: { sources: [...], system_type, label },
//   }
//
//   min_n_per_side: integer
//
//   gaps: { data, measurement, causation, ethics }   — per Tim's
//                                                      gap analysis
//   solution_status: 'prescriptive' | 'predictive_only' | 'not_ready'
//   ecological_prescription: text   — the nature-validated solution
//   human_translation: text          — how to apply it to human systems
//
//   secondary_comparison?: { ... }   — optional second comparison
//                                      (e.g., extant-only filter for MVP)
//
// References:
//   - Original methodology.js for the 7 currently-active dynamics
//   - Tim's gap analysis document for the prescription + gap data
//   - JARVIS_ARCHITECTURE.md for the platform-wide context
// ═══════════════════════════════════════════════════════════

export const DYNAMICS_REGISTRY = [

  // ───────────────────────────────────────────────────────────
  // CLUSTER 1 — ORGANIZATIONAL SURVIVAL
  // ───────────────────────────────────────────────────────────

  {
    id: 'mvp_threshold',
    label: 'Minimum Viable Population Threshold',
    short: 'MVP Threshold',
    cluster: 'organizational_survival',
    version: '1.3',
    status: 'active',
    units: 'recovery ratio (0-1)',
    description:
      'For systems that experienced a documented collapse (trough_year > peak_year ' +
      'AND trough_value <= 85% of peak_value), compute current_value / peak_value — ' +
      'the recovery ratio.',

    metric: {
      type: 'ratio',
      numerator: 'current_value',
      denominator: 'peak_value',
    },

    // Filters applied to raw rows BEFORE metric computation
    filters: [
      { field: 'peak_value',    op: 'gt',  value: 0 },
      { field: 'trough_value',  op: 'gte', value: 0 },
      { field: 'current_value', op: 'gte', value: 0 },
      // Crash floor: trough must be <= 85% of peak (real collapse occurred)
      { field: 'trough_value', op: 'lte', expression: 'peak_value * 0.85' },
      // Temporal: trough came AFTER peak (avoids "all-time low at series start" artifact)
      { field: 'trough_year', op: 'gt', expression: 'peak_year' },
    ],

    required_fields: [
      'system_name', 'source_key',
      'peak_value', 'peak_year',
      'trough_value', 'trough_year',
      'current_value', 'current_year',
    ],

    domains: {
      eco:   { sources: ['lpi', 'ram_legacy', 'modis_ndvi', 'inaturalist'], system_type: 'population',   label: 'Ecological Systems' },
      human: { sources: ['owid', 'fred', 'worldbank'], system_type: 'civilization', label: 'Human Systems' },
    },

    min_n_per_side: 15,

    secondary_comparison: {
      id: 'extant_only',
      label: 'Extant only — eco events with current_value > 0',
      eco_filter: { field: 'current_value', op: 'gt', value: 0 },
    },

    // From Tim's gap analysis document
    gaps: { data: 'low', measurement: 'medium', causation: 'medium', ethics: 'low' },
    solution_status: 'prescriptive',
    ecological_prescription:
      'Managed population supplementation. Bring individuals from outside to boost ' +
      'numbers above threshold. Genetic rescue programs. Conservation breeding.',
    human_translation:
      'PARALLEL, HIGH confidence: ecological and human systems show statistically ' +
      'indistinguishable MVP threshold ratios (eco 0.30 n=16,679 vs human 0.32 n=664, ' +
      'p=0.16, d=-0.05). The dynamic IS universal — the same mathematical floor applies ' +
      'on both sides. Note: this verdict flipped from DIVERGENT to PARALLEL after the ' +
      'RAM Legacy fish-stock dataset was added (Apr 2026); the prior divergence was ' +
      'partly an artefact of eco-side recovery undersampling. The prescription holds in ' +
      'both domains: organizations below MVP need external injection — not just of ' +
      'capital but of people, energy, and network connections. The merger is the ' +
      'conservation breeding program. The strategic partnership is genetic rescue. The ' +
      'community organizer who brings 50 new members is the wildlife manager releasing ' +
      '50 captive-bred individuals. Secondary finding (extant-only filter, eco events ' +
      'with current_value > 0): a small NEAR-PARALLEL gap remains (d=0.22) — surviving ' +
      'systems on the ecological side cluster slightly higher above the floor than ' +
      'surviving human systems do. The gap IS the finding when extinction-state events ' +
      'are excluded.',
  },

  {
    id: 'recovery_asymmetry',
    label: 'Recovery Asymmetry Ratio',
    short: 'Recovery Asymmetry',
    cluster: 'organizational_survival',
    version: '1.0',
    status: 'active',
    units: 'ratio (typically > 1)',
    description:
      'For each system with documented collapse + recovery: ' +
      'asymmetry = collapse_rate / recovery_rate.',

    metric: {
      type: 'asymmetry_ratio',   // value comes from precomputed asymmetry_ratio column
      field: 'asymmetry_ratio',
      sanity_clip: { min: 0, max: 1000 },
    },

    filters: [
      { field: 'asymmetry_ratio', op: 'not_null' },
      { field: 'outcome', op: 'not_in', value: ['failed', 'extinct_local'] },
    ],

    required_fields: [
      'system_name', 'source_key',
      'asymmetry_ratio', 'outcome',
      'collapse_rate', 'recovery_rate',
    ],

    domains: {
      eco:   { sources: ['lpi', 'ram_legacy', 'modis_ndvi', 'inaturalist'], system_type: 'population', label: 'Ecological Systems' },
      // FRED replaces the originally-planned IMF dataset — same use case,
      // larger sample, auto-updates. Recovery Asymmetry events derive from
      // FRED's macroeconomic crash-recovery curves (GDP, employment, etc.)
      human: { sources: ['fred', 'worldbank'],  system_type: 'crisis',     label: 'Human Systems' },
    },

    min_n_per_side: 15,

    gaps: { data: 'medium', measurement: 'medium', causation: 'low', ethics: 'low' },
    solution_status: 'prescriptive',
    ecological_prescription:
      'Active managed recovery. Do not wait for passive regeneration. Intervene at every ' +
      'stage. Remove the stressors. Restore the habitat. Reintroduce the missing elements. ' +
      'Active management dramatically reduces absolute recovery time.',
    human_translation:
      'NEAR-PARALLEL, HIGH confidence: human systems still show ~70% greater recovery ' +
      'asymmetry than ecological systems (eco 35.6 n=22,371 vs human 60.3 n=1,102, ' +
      'p=0, d=-0.29) but the gap narrowed sharply after RAM Legacy fish-stock recoveries ' +
      'were added to the eco sample (Apr 2026 — verdict moved from DIVERGENT to ' +
      'NEAR-PARALLEL). Cohen\'s d=-0.29 is a small effect — statistically distinguishable ' +
      'but practically modest. Read: humans crash sharply and rebuild somewhat more slowly ' +
      'relative to crash speed than biology, but the gap is roughly half what the original ' +
      'eco-undersampled run estimated. The prescription that holds in both domains: rapid ' +
      'intervention compresses absolute recovery time. Don\'t wait for passive regeneration. ' +
      'Remove the stressors. Restore the substrate. Reintroduce the missing elements. ' +
      'Caveat: World Bank dataset still includes many ongoing-crisis countries (Venezuela, ' +
      'Lebanon, Syria, etc.) whose recovery hasn\'t played out — adding closed-window crisis ' +
      'datasets (e.g. Tainter, Diamond) would tighten the estimate further.',
  },

  {
    id: 'tipping_signature',
    label: 'Tipping-Point Signature',
    short: 'Tipping Signature',
    cluster: 'environmental_resource',
    version: '1.0',
    status: 'active',
    units: 'tipping score (0-1)',
    description:
      'Critical Slowing Down (CSD) score per system, computed from the time-series ' +
      'sample. Composite of lag-1 autocorrelation, variance trend, and coefficient of ' +
      'variation.',

    metric: {
      type: 'csd_composite',
      handler: 'tippingSignature',
      weights: { lag1_autocorrelation: 0.5, variance_trend: 0.3, cv: 0.2 },
      min_series_points: 5,
    },

    filters: [
      { field: 'raw_data', op: 'not_null' },
    ],

    required_fields: [
      'system_name', 'source_key', 'peak_value', 'current_value', 'raw_data',
    ],

    domains: {
      eco:   { sources: ['lpi', 'ram_legacy', 'modis_ndvi', 'inaturalist'], system_type: 'population',   label: 'Ecological Systems' },
      // FRED's NFCI (Chicago Fed Financial Conditions) is the canonical
      // financial-system tipping signal in the literature. Adding FRED
      // alongside OWID gives both civilizational + financial substrates.
      human: { sources: ['owid', 'fred', 'worldbank'], system_type: 'civilization', label: 'Human Systems' },
    },

    min_n_per_side: 15,

    gaps: { data: 'medium', measurement: 'high', causation: 'medium', ethics: 'high' },
    solution_status: 'prescriptive',
    ecological_prescription:
      'When warning signals appear — reduce the pressure on the system. Remove stressors. ' +
      'Increase resilience inputs. The system will often stabilize if intervention happens ' +
      'before the tipping point. After the tipping point — it will not.',
    human_translation:
      'Notably, human systems show measurably HIGHER average CSD signatures than ecological ' +
      'systems (eco 0.290 vs human 0.397, DIVERGENT, HIGH confidence) — institutional ' +
      'fragility is more visible in human data than in biological data, which inverts the ' +
      'common assumption that ecology is the more vulnerable substrate. Practical ' +
      'implication: organizations and economies showing warning signals (increasing ' +
      'variance in key metrics, slower recovery from small shocks, oscillation between ' +
      'states) need stressor reduction — not stimulus. The counterintuitive prescription: ' +
      'stop pushing harder. The system is telling you it is close to tipping. Push harder ' +
      'and you will push it over.',
  },

  {
    id: 'post_collapse_overshoot',
    label: 'Post-Collapse Overshoot',
    short: 'Overshoot',
    cluster: 'organizational_survival',
    version: '1.0.1',
    status: 'active',
    units: 'overshoot ratio (>1 = exceeded prior peak)',
    description:
      'For systems where the time-series sample shows a documented crash followed by ' +
      'recovery PAST the previous local peak. Computes overshoot_ratio = ' +
      'post_rebound_peak / pre_crash_peak.',

    metric: {
      type: 'dip_then_rebound',
      handler: 'postCollapseOvershoot',
      crash_floor: 0.85,
      min_series_points: 5,
      exclude_trough_zero: true,
    },

    filters: [
      { field: 'raw_data', op: 'not_null' },
    ],

    required_fields: [
      'system_name', 'source_key',
      'peak_value', 'peak_year',
      'trough_value', 'current_value', 'raw_data',
    ],

    domains: {
      eco:   { sources: ['lpi', 'ram_legacy', 'modis_ndvi', 'inaturalist'], system_type: 'population',   label: 'Ecological Systems' },
      // FRED adds historical macroeconomic recoveries — every recession
      // potentially produces an Overshoot event if recovery exceeded
      // pre-crisis peak (rare but real, e.g. some post-WWII economies).
      human: { sources: ['owid', 'fred', 'worldbank'], system_type: 'civilization', label: 'Human Systems' },
    },

    min_n_per_side: 8,

    gaps: { data: 'medium', measurement: 'medium', causation: 'medium', ethics: 'low' },
    solution_status: 'prescriptive',
    ecological_prescription:
      'Some populations rebound past their pre-crash peak via release from competition ' +
      'or exploitation of post-collapse resource vacuums. The intervention is to identify ' +
      'and protect the conditions that enable overshoot rather than suppressing them.',
    human_translation:
      'Post-crash periods can produce institutional or economic configurations that ' +
      'exceed pre-crash performance — but only if regulatory or competitive constraints ' +
      'that suppressed prior growth are simultaneously removed. Crisis is opportunity ' +
      'IF the binding constraint is also released.',
  },

  // ───────────────────────────────────────────────────────────
  // PENDING — defined but no extractor yet
  // ───────────────────────────────────────────────────────────

  {
    id: 'keystone_cascade',
    label: 'Keystone Cascade Amplification',
    short: 'Keystone Cascade',
    cluster: 'organizational_survival',
    version: 'pending',
    status: 'pending',
    units: '×',
    description:
      'Cascade amplification ratio (downstream impact size / keystone removal size). ' +
      'Requires food-web data (Web of Life) which is not yet wired.',

    metric: { type: 'pending' },
    filters: [],
    required_fields: [],
    domains: {
      eco:   { sources: [], label: 'Ecological Systems' },
      human: { sources: [], label: 'Human Systems' },
    },
    min_n_per_side: 15,

    gaps: { data: 'low', measurement: 'high', causation: 'medium', ethics: 'medium' },
    solution_status: 'prescriptive',
    ecological_prescription:
      'Trophic rewilding. Reintroduce the keystone. Or introduce a functional equivalent ' +
      '— a different species that performs the same regulatory function.',
    human_translation:
      'When a keystone leader, institution, or cultural anchor is lost — the prescription ' +
      'is not to mourn or try to replace them exactly. It is to identify the FUNCTION ' +
      'they served and find a functional equivalent. The personality is not the keystone. ' +
      'The function is.',
  },

  {
    id: 'refugia',
    label: 'Refugia Distribution',
    short: 'Refugia',
    cluster: 'environmental_resource',
    version: 'pending',
    status: 'pending',
    units: 'fraction (0-1)',
    description:
      'Minimum refugia distribution score required for system recovery. ' +
      'Requires per-species refugia mapping from Earth Engine + IUCN range polygons.',

    metric: { type: 'pending' },
    filters: [],
    required_fields: [],
    domains: {
      eco:   { sources: [], label: 'Ecological Systems' },
      human: { sources: [], label: 'Human Systems' },
    },
    min_n_per_side: 15,

    gaps: { data: 'medium', measurement: 'high', causation: 'medium', ethics: 'medium' },
    solution_status: 'prescriptive',
    ecological_prescription:
      'Identify and protect refugia before the collapse — not during. Ensure refugia are ' +
      'geographically and genetically diverse.',
    human_translation:
      'Organizations and communities should deliberately maintain distributed outposts ' +
      'during periods of stability — not scramble to create them during crisis. Build ' +
      'redundancy during abundance.',
  },

  {
    id: 'behavioral_flexibility',
    label: 'Behavioral Flexibility Index',
    short: 'Behavioral Flex',
    cluster: 'organizational_survival',
    version: '1.0.0',
    status: 'active',
    units: 'composite index (0-1)',
    description:
      'Composite index measuring how widely a system can flex while remaining viable. ' +
      'Eco-side: computed from time-series statistics of MODIS NDVI annual means per biome ' +
      '(range, coefficient of variation, recovery responsiveness, distribution entropy). ' +
      'Human-side (TBD next phase): occupational diversity (Theil), geographic mobility, ' +
      'educational attainment dispersion, life-script timing variation across cohorts. ' +
      'High score = system maintains broad behavioral repertoire; low score = monocultural / brittle.',

    // ─── Metric configuration ───────────────────────────────
    // The composite is a weighted sum of 4 sub-metrics computed from
    // each event's raw_data.series (MODIS shape) or raw_data.sample
    // (LPI / RAM Legacy shape — same {year, value}[] structure):
    //
    //   1. range_normalized       (max - min) / max              weight 0.30
    //   2. coefficient_of_variation  std / mean                  weight 0.25
    //   3. recovery_responsiveness   bouncebacks / dips           weight 0.25
    //   4. distribution_entropy      Shannon over 5 value bins    weight 0.20
    //
    // Result is clipped to [0, 1]. Higher = more flexible.
    //
    // Implementation: lib/continuum/simulation/engine.js
    //                 → METRIC_HANDLERS.behavioral_flexibility_composite
    metric: {
      type: 'behavioral_flexibility_composite',
      handler: 'behavioralFlexibilityComposite',
      weights: {
        range_normalized:         0.30,
        coefficient_of_variation: 0.25,
        recovery_responsiveness:  0.25,
        distribution_entropy:     0.20,
      },
      min_series_points: 8,                  // need at least 8 years for stable stats
      entropy_bins:      5,                  // 5-bin histogram for distribution entropy
      recovery_window:   3,                  // years to look ahead after a dip for rebound
      recovery_threshold: 0.95,              // dip "bounced back" if value returns to ≥ 95% of pre-dip level
    },

    filters: [
      { field: 'raw_data', op: 'not_null' },
    ],

    required_fields: [
      'system_name', 'source_key', 'raw_data',
    ],

    domains: {
      // Eco-side LIVE via MODIS NDVI (30 globally-distributed biome
      // regions × 24 years). LPI populations also have time-series
      // samples and can supplement when biome-level NDVI is too coarse
      // for a specific population question.
      eco:   {
        sources: ['modis_ndvi', 'lpi'],
        system_type: 'biome_or_population',
        label: 'Ecological Systems',
      },
      // Human-side TBD — next phase. Operationalization plan:
      //   worldbank: labor force participation by occupation (Theil index),
      //              urbanization rates (geographic mobility proxy)
      //   owid:      time-use & life-event data, age-at-first-marriage
      //              variation across cohorts
      //   fred:      occupation-category employment dispersion (US-only,
      //              extends via OECD STAN for cross-country)
      // Human-side LIVE via worldbank_bf source (4 BF-specific
      // indicators: self-employment, female labor participation,
      // tertiary enrollment, urban population %). Each indicator
      // pulled per country produces one continuum_human_events row;
      // the BF composite handler reads raw_data.sample and computes
      // the same 4 sub-metrics applied to the eco side. owid + fred
      // remain spec'd for future expansion (cohort age-at-marriage
      // std, occupational Theil from US BLS, etc.).
      human: {
        sources: ['worldbank_bf', 'owid', 'fred'],
        system_type: 'civilization_or_organization',
        label: 'Human Systems',
      },
    },

    min_n_per_side: 15,

    // Updated gaps reflecting eco-side being live (MODIS data loaded)
    // but human-side operationalization pending.
    gaps: { data: 'medium', measurement: 'medium', causation: 'low', ethics: 'low' },
    solution_status: 'prescriptive',

    ecological_prescription:
      'Do not optimize a single niche to its maximum efficiency if the environment is ' +
      'unstable. Maintain behavioral repertoire even when current conditions do not ' +
      'require it. The species or biome that maintains broad diet, habitat range, and ' +
      'response repertoire survives stochastic shocks the specialist cannot.',

    human_translation:
      'DIVERGENT, HIGH confidence with universal regional confirmation. Updated ' +
      'cross-domain comparison with full LPI corpus included (n_eco=17,681, ' +
      'n_human=1,382 country×indicator events) gives eco_mean=0.607 vs human_mean=' +
      '0.448, p≈0, Cohen\'s d=0.972 (large effect). Human systems are SYSTEMATICALLY ' +
      'less behaviorally flexible than the ecological baseline. Regional facets ' +
      'show this is not a regional artifact — EVERY World Bank region is DIVERGENT ' +
      'or PARTIAL: North America (n=10, d=1.15, DIVERGENT), East Asia & Pacific ' +
      '(n=115, d=1.07, DIVERGENT), Europe & Central Asia (n=207, d=0.96, DIVERGENT), ' +
      'Latin America & Caribbean (n=134, d=0.94, DIVERGENT), Sub-Saharan Africa ' +
      '(n=179, d=0.74, PARTIAL), MENA-AfPak (n=89, d=0.65, PARTIAL), South Asia ' +
      '(n=24, d=0.61, PARTIAL). The K-monoculture pathology is a HUMAN PHENOMENON, ' +
      'not a Korea phenomenon — Korea (composite 0.445, σ=-0.02 from human mean) ' +
      'is INSIDE the human-typical range; the divergence is the human distribution ' +
      'as a whole sitting below the eco baseline. North America and East Asia show ' +
      'the strongest deficit; Sub-Saharan Africa retains the most flexibility ' +
      'closest to the eco baseline (less industrial monoculture, more occupational ' +
      'and life-script diversity). Korea-specific application unchanged: every ' +
      'layer of Korea\'s reproductive infrastructure (career path, education funnel, ' +
      'childcare model, life-script timing) needs deliberate diversification. The ' +
      'global lesson: every developed-economy region has a behavioral-flexibility ' +
      'deficit relative to ecological systems. The "capability unused today" cost ' +
      'is borne broadly, with Korea, the West, and Latin America as leading-edge ' +
      'cases.',
  },

  // ───────────────────────────────────────────────────────────
  // KOREA-ALIGNED PENDING DYNAMICS
  //
  // These 5 mechanisms appear in Korea Decentralization's
  // scenario.mechanisms list AND map directly onto Korea's 5
  // tipping-point classifications. Each has a real eco mechanism
  // + Korea-specific human translation but the cross-domain metric
  // extractor is pending. Mechanism trace resolves these as proper
  // links (no longer 'definition_pending'); evidence-basis tags as
  // EXTRAPOLATIVE (mechanism-only inference until cross-domain
  // computation lands).
  //
  // Promotion path: each one becomes 'active' when its eco-side
  // metric extractor lands AND a verdict (PARALLEL/NEAR-PARALLEL/
  // DIVERGENT) is assigned from cross-domain comparison.
  // ───────────────────────────────────────────────────────────

  {
    id: 'allee_effect',
    label: 'Allee Effect (Cooperative-Reproduction Threshold)',
    short: 'Allee Effect',
    cluster: 'organizational_survival',
    version: '1.0.0',
    status: 'active',
    units: 'decline acceleration (signed pct/year²; positive = self-amplifying decline)',
    description:
      'Positive density-dependence below a critical threshold: when density drops ' +
      'below the Allee point, per-capita reproduction rate also drops, creating a ' +
      'self-reinforcing decline. Cooperative behaviors (group hunting, cooperative ' +
      'breeding, kin-network childcare) require minimum density to function; below ' +
      'that density they collapse non-linearly and the population can fail even ' +
      'while still numerically above absolute extinction floor. ' +
      'Operationalized as decline_acceleration: split each time series at midpoint, ' +
      'fit least-squares slope to each half normalized by absolute mean, output ' +
      'prior_slope_pct − recent_slope_pct. Positive value = recent half declining ' +
      'faster than prior half = Allee-positive trap.',

    // Phase 1 metric: decline acceleration over the time-series sample.
    // For LPI populations: detects populations whose decline is
    // accelerating (cooperative-breeding collapse) vs equilibrating.
    // For human TFR: Korea is the textbook case (1.7 → 1.5 → 1.0 →
    // 0.78 with recent slope ~3× steeper than prior).
    metric: {
      type: 'decline_acceleration',
      min_series_points: 10,
    },

    filters: [
      { field: 'raw_data', op: 'not_null' },
    ],

    required_fields: [
      'system_name', 'source_key', 'raw_data',
    ],

    domains: {
      // Eco-side: LPI + RAM Legacy populations have time-series samples.
      // MODIS NDVI is biome-level vegetation health — also valid here
      // as biome-scale "cooperative density" proxy.
      eco: {
        sources: ['lpi', 'ram_legacy', 'modis_ndvi'],
        system_type: 'population',
        label: 'Ecological Systems',
      },
      // Human-side LIVE via worldbank_allee source (TFR per country,
      // 1960-present). The decline_acceleration handler reads
      // raw_data.sample and computes the prior-vs-recent-half slope
      // delta. Korea registers ~3× steeper recent slope than prior.
      human: {
        sources: ['worldbank_allee'],
        system_type: 'reproductive_output',
        label: 'Human Systems',
      },
    },
    min_n_per_side: 15,

    // Updated gaps reflecting both sides live. Causation low because
    // the Allee mechanism is a well-established ecological model;
    // measurement medium because slope-of-slope is sensitive to
    // sample length.
    gaps: { data: 'low', measurement: 'medium', causation: 'low', ethics: 'low' },
    solution_status: 'prescriptive',

    ecological_prescription:
      'Translocation or captive breeding to boost density above the Allee threshold. ' +
      'Engineered habitat compression to artificially raise local density. The ' +
      'intervention is not "more individuals overall" — it is "above-threshold ' +
      'density in cooperative units."',

    human_translation:
      'Korea fertility trap is the textbook Allee signature in human form. The ' +
      'multi-generational kin network — historically the cooperative-reproduction ' +
      'infrastructure that distributed childcare costs across grandmothers, aunts, ' +
      'cousins, neighbors — has collapsed below operational threshold. Nuclear ' +
      'families bear costs that previously distributed across the network, making ' +
      'each marginal child increasingly expensive. Cash transfers do not restore ' +
      'cooperative density. The architectural intervention is engineered cooperative ' +
      'infrastructure: community childcare networks, multi-generational housing ' +
      'developments, kin-substitute networks at neighborhood scale. The decentralization ' +
      'scenario succeeds only if satellite cities are designed to RECONSTITUTE ' +
      'cooperative density, not just relocate population. ' +
      'CROSS-DOMAIN MEASUREMENT (n_eco=15,920 expanded, n_human=261): PARALLEL HIGH ' +
      '(p≈3.8e-6, Cohen\'s d≈0.05). REGIONAL FACETS confirm the parallel verdict ' +
      'is structural, not a sampling artifact: every World Bank region shows ' +
      'PARALLEL with eco baseline (Sub-Saharan Africa d=0.10, MENA-AfPak d=0.07, ' +
      'East Asia & Pacific d=0.03, Europe & Central Asia d=0.03, Latin America & ' +
      'Caribbean d=0.03). The Allee mechanism operates in similar aggregate ' +
      'magnitude across all human regions and across both domains. The Korea-' +
      'trap signal is COUNTRY-LEVEL, not regional. Korea (+2.50σ above human ' +
      'global mean, +1.7σ within East Asia regional pool) sits with Hong Kong ' +
      '(0.069), Singapore (0.054), Cuba (0.052), Thailand (0.050), China (0.050) — ' +
      'all "completed-transition still-falling" cases scattered across regions. ' +
      'The bottom of the distribution is the active-transition cluster: Ethiopia ' +
      '(-0.023), Lao PDR (-0.021), South Sudan (-0.021), Yemen (-0.020). ' +
      'IMPLICATION: the kin-network reconstitution intervention is country-' +
      'specific (preventive for most, corrective only for the East-Asia/ ' +
      'Caribbean post-transition tail). Trajectory-shape sub-classification ' +
      '(continuous-drift like Korea vs plateau-then-shock like Greenland) is V2 ' +
      'work — the metric currently groups by trajectory shape, not by causal ' +
      'mechanism, so the top-10 includes both Korea-style traps and small-' +
      'population artifacts that should be reasoned about separately.',
  },

  {
    id: 'r_k_strategy_split',
    label: 'R/K Strategy Environment Mismatch',
    short: 'R/K Mismatch',
    cluster: 'organizational_survival',
    version: '0.1.0',
    // 2026-04-28: investigated promotion to active. The conceptually
    // correct r/K metric requires species-level metadata (body mass,
    // generation time, mean offspring count) for the eco side that LPI's
    // raw_data does not carry. Available eco proxies (population
    // stability via CV, peak-to-current ratio) are statistically
    // redundant with behavioral_flexibility per Finding 1's r=0.71
    // correlation analysis — promoting with weaker proxies would create
    // a measurement that double-counts an existing dimension. Promotion
    // deferred to V2 with proper species-trait integration (IUCN trait
    // database, COMPADRE plant-trait database, or AnAge animal-aging
    // database). Human-side metric IS computable (school_life × 1/TFR)
    // but cross-domain symmetry requires both sides.
    status: 'pending_implementation',
    units: 'mismatch index (signed; positive = K-strategist in R-environment)',
    description:
      'r-strategy = many offspring, low investment per offspring (works in unstable, ' +
      'high-mortality environments). K-strategy = few offspring, high investment per ' +
      'offspring (works in stable, low-mortality environments). When the environment ' +
      'shifts but the strategy does not, the population becomes mismatched. K-strategists ' +
      'in increasingly volatile environments produce too few offspring to absorb shocks; ' +
      'r-strategists in newly-stable environments over-produce and exhaust resources.',

    metric: { type: 'pending' },
    filters: [],
    required_fields: [],
    domains: {
      eco: {
        sources: ['lpi', 'ram_legacy'],
        system_type: 'population',
        label: 'Ecological Systems',
      },
      human: {
        sources: ['worldbank', 'owid'],
        system_type: 'reproductive_strategy',
        label: 'Human Systems',
      },
    },
    min_n_per_side: 15,

    gaps: { data: 'medium', measurement: 'medium', causation: 'medium', ethics: 'low' },
    solution_status: 'prescriptive',

    ecological_prescription:
      'Behavioral flexibility selection — favor genotypes capable of switching between ' +
      'r and K strategies based on environmental signals. Diversify the strategy mix ' +
      'within the population so the system has cohorts ready for either environmental ' +
      'regime. Phenotypic plasticity is the deep solution; pure r or pure K is the ' +
      'fragile solution.',

    human_translation:
      'Korea has converged on extreme K-strategy reproduction: single child or zero ' +
      'children, with massive per-child investment (private tutoring, school-zone ' +
      'housing premiums, multi-decade career-prep). The environment is increasingly ' +
      'volatile (job-market instability, climate stress, political uncertainty), making ' +
      'this K-strategy mismatched. The required adaptation is NOT "have more kids" ' +
      '(which assumes pure r-shift). It is diversification of life paths — making ' +
      'multiple life-script options viable simultaneously: smaller-investment family ' +
      'structures, community-supported child-rearing, strategy diversity within ' +
      'cohorts. The K-monoculture itself is the trap.',
  },

  {
    id: 'age_structure_trap',
    label: 'Age Structure Inversion Trap',
    short: 'Age Structure',
    cluster: 'organizational_survival',
    version: '1.0.0',
    status: 'active',
    units: 'inversion score (1 − current/peak; higher = aged/depleted)',
    description:
      'When the share of post-reproductive individuals exceeds the share of ' +
      'reproductive-age individuals plus the workforce that supports them, the ' +
      'population enters self-amplifying decline. Measured here as ' +
      '1 − (current youth-share / peak youth-share) per country and ' +
      '1 − (current population / peak population) per LPI population. Both ' +
      'capture "how far the system has fallen from its demographic peak." ' +
      'High score = aged / depleted; low score = at or near peak.',

    // Phase 1 metric: read peak_value + current_value directly from each
    // event row (already populated by loaders). Phase 2 could expand to
    // a dedicated age_pyramid_inversion metric using paired indicators
    // (% over 65 / % under 15) but the inverse-recovery-ratio formula
    // captures the same trap signature with simpler data plumbing.
    metric: {
      type: 'inverse_recovery_ratio',
    },

    filters: [
      { field: 'peak_value', op: 'gt', value: 0 },
      { field: 'current_value', op: 'not_null' },
    ],

    required_fields: [
      'system_name', 'source_key', 'peak_value', 'current_value',
      'peak_year', 'current_year',
    ],

    domains: {
      // Eco-side LIVE: LPI populations. Each population's peak/current
      // ratio captures how depleted it is. Many LPI rows are stable
      // (high recovery ratio = low inversion). Declining populations
      // produce high inversion scores.
      eco: {
        sources: ['lpi'],
        system_type: 'population',
        label: 'Ecological Systems',
      },
      // Human-side LIVE via worldbank_age source. Indicator
      // SP.POP.0014.TO.ZS captures children-share trajectory; declining
      // values from peak signal age inversion. Korea is the trap case:
      // peak ~40% (1970), current 10.5% → inversion score ~0.74.
      human: {
        sources: ['worldbank_age'],
        system_type: 'demographic_pyramid',
        label: 'Human Systems',
      },
    },
    min_n_per_side: 15,

    gaps: { data: 'low', measurement: 'low', causation: 'medium', ethics: 'high' },
    solution_status: 'prescriptive',

    ecological_prescription:
      'External infusion of reproductive-age individuals (immigration analog: ' +
      'introduction of breeding-age individuals from external populations to restore ' +
      'the reproductive-age share). Demographic engineering of cohort timing if ' +
      'immigration is unavailable.',

    human_translation:
      'NEAR-PARALLEL, HIGH confidence: cross-domain comparison shows ecological and ' +
      'human systems both carry substantial peak-to-current inversion (eco mean=' +
      '0.516 n=35,963 LPI populations vs human mean=0.380 n=261 countries on % ' +
      'under 15, p=0, Cohen\'s d=0.443). Wild populations average ~52% below ' +
      'historical peaks (LPI biased toward at-risk pops); country children-shares ' +
      'average ~38% below their peaks. REGIONAL FACETS reveal the global verdict ' +
      'is misleading: Sub-Saharan Africa is DIVERGENT in the OPPOSITE direction ' +
      '(n=48, d=1.16, mean=0.174 — still demographically YOUNG, far from peak ' +
      'depletion). All other regions are NEAR-PARALLEL or PARALLEL: Middle East/' +
      'North Africa (d=0.379), Europe & Central Asia (d=0.302), East Asia & Pacific ' +
      '(d=0.250), Latin America & Caribbean (d=-0.002, exact PARALLEL). The signal ' +
      'is the SSA outlier — the rest of the world is converging toward the eco-' +
      'depletion baseline. Korea sits at inversion score 0.757 (+2.12σ above the ' +
      'human global mean, +1.8σ above East Asia regional mean) — the trap case ' +
      'the framing was built for. Each working-age cohort supports a growing ' +
      'dependent ratio, depressing both fertility and per-capita next-generation ' +
      'investment. Without external infusion (immigration of reproductive-age ' +
      'people), the trap deepens with each cohort regardless of pro-natal policy. ' +
      'Korea has historically resisted immigration on cultural grounds; the ' +
      'demographic math says either immigration accelerates substantially or ' +
      'population stabilizes ~50% lower over 50 years. Ethics note (gaps.ethics= ' +
      'high): the immigration framing has political and cultural-humility dimensions ' +
      'the simulator must surface, not assume. POLICY RELEVANCE: SSA\'s position ' +
      'as the only DIVERGENT-opposite region means the global pro-natal demographic ' +
      'reservoir for the rest of the planet is concentrated there — interventions ' +
      'must reckon with this when discussing reproductive infrastructure across ' +
      'regions.',
  },

  {
    id: 'carrying_capacity_ceiling',
    label: 'Carrying Capacity (Urban K-Strategy Density Ceiling)',
    short: 'Carrying Capacity',
    cluster: 'environmental_resource',
    version: '0.1.0',
    // 2026-04-28: investigated. The metric requires "K-strategy
    // demand per capita" + "environmental capacity" — neither has a
    // clean direct measurement. Density alone (worldbank EN.POP.DNST)
    // is computable but maps to multiple dynamics already (allee_effect
    // on the demographic axis, behavioral_flexibility on the urbanization
    // proxy). Promoting with density-as-proxy would correlate r > 0.7
    // with existing dynamics per Finding 1. Promotion deferred until V2
    // ecosystem-services data integration (footprint per capita ÷
    // biocapacity per region from Global Footprint Network).
    status: 'pending_implementation',
    units: 'ratio (current density × strategy demand / environmental capacity)',
    description:
      'Every environment has a carrying capacity for the resources required by a given ' +
      'reproductive strategy. K-strategy reproduction requires much higher per-capita ' +
      'resource availability than r-strategy. When urban density × K-strategy demand ' +
      'exceeds environmental carrying capacity, fertility collapses to maintain ' +
      'individual-level investment. The collapse is not from absolute resource scarcity ' +
      '— it is from the strategy-density product exceeding what the environment can ' +
      'sustain at the chosen investment level.',

    metric: { type: 'pending' },
    filters: [],
    required_fields: [],
    domains: {
      eco: {
        sources: ['modis_ndvi', 'lpi'],
        system_type: 'population',
        label: 'Ecological Systems',
      },
      human: {
        sources: ['worldbank', 'fred'],
        system_type: 'urban_density',
        label: 'Human Systems',
      },
    },
    min_n_per_side: 15,

    gaps: { data: 'low', measurement: 'medium', causation: 'low', ethics: 'low' },
    solution_status: 'prescriptive',

    ecological_prescription:
      'Reduce density via dispersal — the original ecological intervention for ' +
      'density-stress collapse. Alternatively, shift to less resource-intensive ' +
      'reproductive strategies (lower per-offspring investment), which raises the ' +
      'ceiling without dispersal.',

    human_translation:
      "Seoul's density (~16,000 persons/km²) multiplied by Korea's K-strategy " +
      'reproductive demands (private tutoring, ultra-competitive housing for school ' +
      'zones, multi-decade career-prep investment) exceeds the city\'s carrying ' +
      'capacity for reproductive-age investment. The Korea Decentralization scenario ' +
      'IS the dispersal intervention, mapped onto the ecological prescription. Whether ' +
      'it works depends on a structural question the simulator must surface: do the ' +
      'satellite cities reduce density WITHOUT preserving the K-strategy demand pattern? ' +
      'If satellites become bedroom communities for Seoul (preserved demand pattern), ' +
      'the ceiling does not lift. If they enable a different reproductive strategy mix ' +
      '(lower per-child investment + more children + community support), the ceiling ' +
      'lifts and the intervention works.',
  },

  {
    id: 'conservation_of_information',
    label: 'Conservation of Information (Generational Replacement)',
    short: 'Information Conservation',
    cluster: 'organizational_survival',
    version: '0.1.0',
    // 2026-04-28: investigated. The metric (cohort N+1 / cohort N
    // size) is essentially TFR ÷ replacement, which is already the
    // signal driving allee_effect (decline_acceleration on TFR series).
    // Replacement-ratio computed independently would correlate r > 0.85
    // with allee_effect per Finding 1's correlation logic. Promotion
    // deferred until V2 adds a genuinely orthogonal information-loss
    // signal: language extinction rate (Glottolog already wired but
    // not yet a metric handler), cultural-knowledge attrition surveys,
    // or skill-transmission longitudinal data.
    status: 'pending_implementation',
    units: 'replacement ratio (cohort N+1 size / cohort N size)',
    description:
      'Tim Mai\'s cross-reference: biological systems require successive cohorts to ' +
      'transmit accumulated information (genes, behaviors, knowledge, institutional ' +
      'memory). When generational replacement breaks down (cohort N+1 significantly ' +
      'smaller than N), accumulated information loss accelerates super-linearly ' +
      'because fewer carriers means narrower coverage of behavioral repertoire and ' +
      'cultural knowledge. Below replacement rate, the system loses information ' +
      'faster than it can re-derive it.',

    metric: { type: 'pending' },
    filters: [],
    required_fields: [],
    domains: {
      eco: {
        sources: ['lpi', 'ram_legacy'],
        system_type: 'population',
        label: 'Ecological Systems',
      },
      human: {
        sources: ['worldbank', 'owid'],
        system_type: 'cohort_replacement',
        label: 'Human Systems',
      },
    },
    min_n_per_side: 15,

    gaps: { data: 'high', measurement: 'high', causation: 'medium', ethics: 'low' },
    solution_status: 'prescriptive',

    ecological_prescription:
      'Preserve biodiversity reservoirs even at minimal viability — the information ' +
      'they carry can be re-distributed if conditions improve. Avoid functional ' +
      'extinction (where individuals exist but cannot transmit). Genetic banking, ' +
      'seed vaults, knowledge-preservation infrastructure are the analog interventions.',

    human_translation:
      'Korea\'s TFR of 0.78 means each generation is roughly half the size of the ' +
      'previous. Within 2-3 generations, this catastrophically erodes accumulated ' +
      'cultural and institutional information — fewer carriers of language nuance, ' +
      'professional craft, regional dialect, family lineage knowledge. The phosphorus-' +
      'cycle framing surfaces: external information injection (immigration, knowledge-' +
      'transfer programs, formal cultural-preservation infrastructure) becomes ' +
      'mathematically necessary, not optional. The system cannot self-sustain its own ' +
      'information at this replacement rate; preservation infrastructure has to ' +
      'compensate for what the smaller cohort cannot carry.',
  },

  // ───────────────────────────────────────────────────────────
  // ROADMAP — Tim's 30-list, items not yet built
  // These are placeholder entries with prescription + gap data
  // from the document. status: 'not_ready' tells the engine to
  // skip them but they're visible in the roadmap UI.
  // ───────────────────────────────────────────────────────────

  // (Roadmap entries 5-30 from the document would be added here
  //  as we work through them. Stubs only — no metric implementation
  //  required until they become 'pending' or 'active'.)

];

// ── Lookup helpers ─────────────────────────────────────────
export const DYNAMICS_BY_ID = Object.fromEntries(
  DYNAMICS_REGISTRY.map(d => [d.id, d])
);

export function getActiveDynamics() {
  return DYNAMICS_REGISTRY.filter(d => d.status === 'active');
}

export function getPendingDynamics() {
  return DYNAMICS_REGISTRY.filter(d => d.status === 'pending');
}

export function getDynamicsByCluster() {
  const out = {};
  for (const d of DYNAMICS_REGISTRY) {
    (out[d.cluster] ||= []).push(d);
  }
  return out;
}

// ── Backward-compat snapshot for continuum_runs.methodology ──
// Mirrors the shape that snapshotMethodology() in the legacy
// methodology.js produced, so existing run rows + downstream
// readers don't break during the migration.
export function snapshotRegistry() {
  const out = {};
  for (const d of DYNAMICS_REGISTRY) {
    out[d.id] = {
      version: d.version,
      status: d.status,
      eco_sources:   d.domains.eco?.sources   || [],
      human_sources: d.domains.human?.sources || [],
      min_n_per_side: d.min_n_per_side,
      metric_type: d.metric?.type,
      cluster: d.cluster,
      solution_status: d.solution_status,
      gaps: d.gaps,
    };
  }
  return out;
}
